/**
 * EditorHeader Component
 * Top header with logo, zoom controls, and action buttons
 */

import { ReactNode, useState, useRef, useEffect } from 'react'
import { ZoomControls } from './ZoomControls'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'
import { useProjectStore } from '../../../map-editor-shared/store/projectStore'
import styles from './EditorHeader.module.css'

export type ViewMode = 'edit' | 'viewer'

interface EditorHeaderProps {
  loadedFileName: string | null
  zoom: number
  theme: 'light' | 'dark'
  hasObjectTypes: boolean
  viewMode: ViewMode
  /** 뷰어 모드 토글 버튼을 누르면 호출. 볼 데이터가 없으면(hasMapData=false) 버튼을 숨긴다. */
  onViewModeChange: (mode: ViewMode) => void
  hasMapData: boolean
  onZoomIn: () => void
  onZoomOut: () => void
  onRotate: () => void
  onFitToScreen: () => void
  onUploadClick: () => void
  onSave: () => void
  onClearCanvas: () => void
  onExport: () => void
  onThemeToggle: () => void
  onBackToProjects: () => void
  /** Optional toolbar slot rendered next to Export. Used by excel-io for the Excel menu. */
  toolbarExtras?: ReactNode
}

export function EditorHeader({
  loadedFileName,
  zoom,
  theme,
  hasObjectTypes,
  viewMode,
  onViewModeChange,
  hasMapData,
  onZoomIn,
  onZoomOut,
  onRotate,
  onFitToScreen,
  onUploadClick,
  onSave,
  onClearCanvas,
  onExport,
  onThemeToggle,
  onBackToProjects,
  toolbarExtras,
}: EditorHeaderProps) {
  // Floor name editing state
  const [isEditingFloor, setIsEditingFloor] = useState(false)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Floor store
  const currentFloor = useFloorStore(state => state.currentFloor)
  const currentLot = useProjectStore(state => state.currentLot)
  const floors = useFloorStore(state =>
    currentLot ? state.getFloorsByLotId(currentLot) : []
  )
  const updateFloor = useFloorStore(state => state.updateFloor)

  const floor = floors.find(f => f.id === currentFloor)
  const currentFloorName = floor?.name || '층 없음'

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (isEditingFloor && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditingFloor])

  const handleDoubleClick = () => {
    if (floor) {
      setEditValue(floor.name)
      setIsEditingFloor(true)
    }
  }

  const handleSaveEdit = () => {
    if (!floor || !editValue.trim()) {
      setIsEditingFloor(false)
      return
    }

    if (editValue.trim() !== floor.name) {
      updateFloor(floor.id, { name: editValue.trim() })
    }
    setIsEditingFloor(false)
  }

  const handleCancelEdit = () => {
    setIsEditingFloor(false)
    setEditValue('')
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSaveEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancelEdit()
    }
  }

  const handleDisplayKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleDoubleClick()
    }
  }

  const renderFloorNameEditor = () => {
    if (isEditingFloor) {
      return (
        <div className={styles.floorNameContainer}>
          <span className={styles.floorLabel}>층 이름:</span>
          <input
            ref={inputRef}
            type="text"
            className={styles.floorNameInput}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleInputKeyDown}
            onBlur={handleSaveEdit}
            aria-label="Edit floor name"
            placeholder="층 이름 입력"
          />
        </div>
      )
    }

    return (
      <div
        className={styles.floorNameDisplay}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleDisplayKeyDown}
        role="button"
        tabIndex={0}
        aria-label={`Current floor: ${currentFloorName}. Double-click to edit.`}
      >
        <span className={styles.floorLabel}>층 이름:</span>
        <span className={styles.floorValue}>{currentFloorName}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.6">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </div>
    )
  }

  return (
    <header className={styles.header}>
      <div className={styles.logo}>
        <button onClick={onBackToProjects} className={styles.backButton} title="Dashboard">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 7l6-3 6 3 6-3v13l-6 3-6-3-6 3V7z" />
        </svg>
        Dashboard
        {loadedFileName && (
          <span className={styles.fileName}>({loadedFileName})</span>
        )}
      </div>

      {/* Floor name editor section */}
      <div className={styles.floorSection}>
        {renderFloorNameEditor()}
      </div>

      {hasMapData && (
        <button
          type="button"
          onClick={() => onViewModeChange(viewMode === 'edit' ? 'viewer' : 'edit')}
          className={styles.toolButton}
          title={viewMode === 'edit' ? '뷰어로 보기(읽기전용)' : '에디터에서 편집'}
        >
          {viewMode === 'edit' ? '👁 뷰어로 보기' : '✏️ 에디터 모드'}
        </button>
      )}

      <div className={styles.toolbar}>
        <ZoomControls
          zoom={zoom}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          onRotate={onRotate}
          onFitToScreen={onFitToScreen}
        />

        <div className={styles.divider} />

        <button onClick={onSave} className={`${styles.toolButton} ${styles.primaryButton}`} title="Save Map">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
          Save
        </button>

        <button
          onClick={onUploadClick}
          className={styles.toolButton}
          title={hasObjectTypes ? "Upload CSV" : "객체 타입을 먼저 생성해주세요"}
          disabled={!hasObjectTypes}
          style={{
            display: 'none'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          Upload CSV
        </button>

        <button onClick={onClearCanvas} className={styles.toolButton} title="Clear Canvas">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
          Clear
        </button>

        <div className={styles.divider} />

        <button onClick={onExport} className={styles.toolButton} title="Export Map Data">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export
        </button>

        {toolbarExtras}

        <div className={styles.divider} />

        <button onClick={onThemeToggle} className={styles.toolButton} title={`Switch to ${theme === 'light' ? 'Dark' : 'Light'} Mode`}>
          {theme === 'light' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
          )}
        </button>
      </div>
    </header>
  )
}
